import { Component, Element, Event, EventEmitter, h, Host, Prop, State } from '@stencil/core';
import { POINTER_EVENTS, PointerEvent } from '../../models/pointers.types';
import { Target, TARGETS } from '../../models/targets.types';
import { ICON_TYPES_BASIS, IconTypeBasis } from '../icon/models/icon.types';
import {
  LINK_ALIGNMENTS,
  LINK_APPEARANCES,
  LINK_ICON_POSITIONS,
  LINK_SIZES,
  LinkAlignment,
  LinkAppearance,
  LinkColors,
  LinkPositions,
  LinkSize,
  LinkSizeSnowflake,
} from './models/link.types';

@Component({
  tag: 'wink-link',
  styleUrl: 'styles/link.default.scss',
  shadow: true,
})
export class Link {
  @Element() host!: HTMLWinkLinkElement;

  @Prop({ reflect: true }) appearance: LinkAppearance = LINK_APPEARANCES.default;
  @Prop() size: LinkSize | LinkSizeSnowflake = LINK_SIZES.inherit;
  @Prop() icon?: string;
  @Prop() iconPosition: LinkPositions = LINK_ICON_POSITIONS.left;
  @Prop() iconAppearance: IconTypeBasis = ICON_TYPES_BASIS.default;
  @Prop() isDisabled = false;
  @Prop() pointerEvent: PointerEvent = POINTER_EVENTS.auto;
  @Prop() href = '';
  @Prop() target: Target = TARGETS.self;
  @Prop() isActive = false;
  @Prop() alignment: LinkAlignment = LINK_ALIGNMENTS.left;
  @Prop() linkColor?: LinkColors;

  @State() hasSlottedIcon = false;

  @Event() winkLinkClicked!: EventEmitter<string>;

  private slotLinkIconElement!: HTMLSlotElement;
  private slotIconElement!: HTMLSlotElement;

  private getTypeClasses() {
    return this.appearance !== LINK_APPEARANCES.default ? `${this.appearance}` : '';
  }

  private getClasses() {
    const sizeClasses = `font--${this.size}`;
    return {
      link: true,
      [sizeClasses]: true,
      [this.getTypeClasses()]: true,
      [`link-with-icon link-with-icon-${this.iconPosition}`]: (this.icon !== '' && this.icon !== undefined) || this.hasSlottedIcon,
      'link-with-icon-backdrop': this.hasBackdrop(),
      disabled: this.isDisabled,
      ['color--' + this.linkColor]: this.linkColor !== undefined,
      active: this.isActive && this.appearance === LINK_APPEARANCES.inPageNavigation,
    };
  }

  private getAttributes() {
    const attributes: {
      href?: string;
      target: string;
      tabindex?: number;
      'aria-disabled'?: string;
      role?: string;
    } = {
      target: this.target,
    };

    if (this.pointerEvent === POINTER_EVENTS.none || this.isDisabled) {
      attributes.tabindex = -1;
    }

    if (this.isDisabled) {
      attributes['aria-disabled'] = 'true';
    }

    if (this.href && this.href.trim() !== '') {
      attributes.href = this.href;
    } else {
      attributes.role = 'button';
      console.warn('wink-link: href is empty. Rendering as a button for accessibility.');
    }

    return attributes;
  }

  private hasBackdrop() {
    return this.iconAppearance !== ICON_TYPES_BASIS.default && Object.values(ICON_TYPES_BASIS).includes(this.iconAppearance);
  }

  private onClick(event: MouseEvent) {
    if (this.pointerEvent === POINTER_EVENTS.none) {
      event.preventDefault();
      return;
    }
    this.winkLinkClicked.emit(this.href);
  }

  private checkSlotContent() {
    const slottedNodesIcon = this.slotIconElement.assignedNodes?.({ flatten: true }) || [];
    const slottedNodesLinkIcon = this.slotLinkIconElement.assignedNodes?.({ flatten: true }) || [];

    this.hasSlottedIcon = slottedNodesIcon.length > 0 || slottedNodesLinkIcon.length > 0;
  }

  componentDidLoad() {
    this.checkSlotContent();
  }

  render() {
    return (
      <Host part="link" {...(this.pointerEvent === POINTER_EVENTS.none ? { tabindex: -1 } : null)}>
        <a class={this.getClasses()} onClick={(event) => this.onClick(event)} {...this.getAttributes()}>
          <slot name="link-icon" ref={(el) => (this.slotLinkIconElement = el as HTMLSlotElement)} />
          <slot name="icon" ref={(el) => (this.slotIconElement = el as HTMLSlotElement)} />
          <div id="link-content">
            <slot />
          </div>
        </a>
      </Host>
    );
  }
}
